import React, { useState, useEffect, useCallback } from 'react';
import { Box, Paper, Typography, Divider, Tooltip } from '@mui/material';
import FBOButton from 'ui/theme/components/FBOButton/FBOButton';
import {
  LabelElement,
  TableData,
  isTableElement,
  isTextElement,
  isQRCodeElement,
  isBarcodeElement,
  isImageElement,
  isShapeElement,
  TextElementProps,
  ShapeElementProps,
  isGroupElement,
  isAdvancedTableElement,
  AdvancedTableData,
  DynamicDataField,
} from '../../types';
import { IconNames } from 'ui/theme';
import DynamicAutocomplete from './components/DynamicAutocomplete';
import QRCodeProperties from './panels/QRCodeProperties';
import BarcodeProperties from './panels/BarcodeProperties';
import TableProperties from './panels/TableProperties';
import TextProperties from './panels/TextProperties';
import ShapeProperties from './panels/ShapeProperties';
import ImageProperties from './panels/ImageProperties';
import GroupProperties from './panels/GroupProperties';
import AdvancedTableProperties from './panels/AdvancedTableProperties';
import MultipleSelectedElementsProperties from './panels/MultipleSelectedElementsProperties';

interface PropertiesPanelProps {
  selectedElements: LabelElement[] | null;
  onUpdateElement: (
    id: string,
    updates: Partial<LabelElement>,
    updateType?: string
  ) => void;
  onUpdateMultipleElements: (newElements: LabelElement[]) => void;
  onDeleteElement: () => void;
  onDuplicateElement: (() => void) | undefined;
  onBringToFront: () => void;
  onSendToBack: () => void;
  onUngroup: () => void;
  canvasWidth?: number;
  availableFields: DynamicDataField[];
}

// Define the default AdvancedTableData object as a module-level constant
const DEFAULT_ADVANCED_TABLE_DATA: AdvancedTableData = {
  numRows: 4,
  numColumns: 4,
  columnWidths: [100, 100, 100, 100],
  rowHeights: [75, 75, 75, 75],
  data: [
    [
      { content: 'Cell 1-1' },
      { content: 'Cell 1-2' },
      { content: 'Cell 1-3' },
      { content: 'Cell 1-4' },
    ],
    [
      { content: 'Cell 2-1' },
      { content: 'Cell 2-2' },
      { content: 'Cell 2-3' },
      { content: 'Cell 2-4' },
    ],
    [
      { content: 'Cell 3-1' },
      { content: 'Cell 3-2' },
      { content: 'Cell 3-3' },
      { content: 'Cell 3-4' },
    ],
    [
      { content: 'Cell 4-1' },
      { content: 'Cell 4-2' },
      { content: 'Cell 4-3' },
      { content: 'Cell 4-4' },
    ],
  ],
  headerRow: true,
  headerRowHeight: 30,
  headers: [
    { key: 'price', label: 'Price' },
    { key: 'quantity', label: 'Quantity' },
    { key: 'name', label: 'Name' },
    { key: 'description', label: 'Description' },
  ],
  headerFontFamily: 'Arial',
  headerFontSize: 12,
  headerBackgroundColor: '#f0f0f0',
  headerTextColor: '#000000',
  defaultCellFontFamily: 'Arial',
  defaultCellFontSize: 10,
  defaultCellBackgroundColor: '#ffffff',
  defaultCellTextColor: '#333333',
  borderColor: '#cccccc',
  borderWidth: 1,
  showGrid: true,
  highlightColor: '#007bff',
};

const PropertiesPanel: React.FC<PropertiesPanelProps> = ({
  selectedElements,
  onUpdateElement,
  onUpdateMultipleElements,
  onDeleteElement,
  onDuplicateElement,
  onBringToFront,
  onSendToBack,
  onUngroup,
  canvasWidth,
  availableFields,
}) => {
  // Break up state into logical groups for better organization and live updates
  // Text properties
  const [textProps, setTextProps] = useState<TextElementProps>({
    id: '',
    type: 'text',
    x: 0,
    y: 0,
    width: 0,
    height: 0,
    text: '',
    fontSize: 16,
    fontFamily: 'Arial',
    fill: '#000000',
    backgroundColor: 'transparent',
  });

  // Line and shape properties
  const [lineProps, setLineProps] = useState<ShapeElementProps>({
    id: '',
    type: 'line',
    x: 0,
    y: 0,
    width: 0,
    height: 0,
    lineWidth: 1,
    stroke: '#000000',
    fill: 'transparent',
  });

  // Common properties for all elements
  const [commonProps, setCommonProps] = useState({
    rotation: 0,
  });

  // Table properties
  const [tableProps, setTableProps] = useState<TableData>({
    rows: 3,
    columns: 3,
    headerTexts: ['', '', ''],
    headerRow: true,
    showGrid: true,
    headerRowHeight: 40,
    headerFontSize: 14,
    headerFontFamily: 'Arial',
    headerTextColor: '#000000',
    headerBackgroundColor: '#f5f5f5',
    backgroundColor: '#ffffff',
    highlightColor: '#f0f0f0',
    highlightedRows: [] as number[],
    highlightedColumns: [] as number[],
  });

  // Image properties
  const [imageProps, setImageProps] = useState({
    src: '',
  });

  // QR Code properties
  const [qrProps, setQrProps] = useState({
    qrValue: '',
  });

  // Barcode properties
  const [barcodeProps, setBarcodeProps] = useState({
    barcodeValue: '',
  });

  const hasMultipleSelection =
    selectedElements !== null && selectedElements.length > 1;
  const selectedSingleElement = !hasMultipleSelection
    ? selectedElements?.[0]
    : null;

  // Update state when selected element changes
  useEffect(() => {
    if (selectedSingleElement) {
      // Update common properties for all elements
      setCommonProps({
        rotation: selectedSingleElement.rotation || 0,
      });

      // Update text properties
      if (isTextElement(selectedSingleElement)) {
        setTextProps({
          id: selectedSingleElement.id,
          type: selectedSingleElement.type,
          x: selectedSingleElement.x,
          y: selectedSingleElement.y,
          width: selectedSingleElement.width,
          height: selectedSingleElement.height,
          text: selectedSingleElement.text || '',
          fontSize: selectedSingleElement.fontSize || 16,
          fontFamily: selectedSingleElement.fontFamily || 'Arial',
          fill: selectedSingleElement.fill || '#000000',
          backgroundColor:
            selectedSingleElement.backgroundColor || 'transparent',
        });
      }

      // Update line properties for shapes
      if (isShapeElement(selectedSingleElement)) {
        setLineProps({
          id: selectedSingleElement.id,
          type: selectedSingleElement.type,
          x: selectedSingleElement.x,
          y: selectedSingleElement.y,
          width: selectedSingleElement.width,
          height: selectedSingleElement.height,
          lineWidth: selectedSingleElement.lineWidth || 1,
          stroke: selectedSingleElement.stroke || '#000000',
          fill: selectedSingleElement.fill || 'transparent',
        });
      }

      // Update table properties
      if (isTableElement(selectedSingleElement)) {
        setTableProps({
          rows: selectedSingleElement.tableData?.rows || 3,
          columns: selectedSingleElement.tableData?.columns || 3,
          headerTexts:
            selectedSingleElement.tableData?.headerTexts ||
            Array(selectedSingleElement.tableData?.columns || 3).fill('Header'),
          headerRow: selectedSingleElement.tableData?.headerRow || false,
          showGrid: selectedSingleElement.tableData?.showGrid ?? true,
          headerRowHeight:
            selectedSingleElement.tableData?.headerRowHeight || 40,
          headerFontSize: selectedSingleElement.tableData?.headerFontSize || 16,
          headerFontFamily:
            selectedSingleElement.tableData?.headerFontFamily || 'Arial',
          headerTextColor:
            selectedSingleElement.tableData?.headerTextColor || '#000000',
          headerBackgroundColor:
            selectedSingleElement.tableData?.headerBackgroundColor || '#f0f0f0',
          backgroundColor:
            selectedSingleElement.tableData?.backgroundColor || 'transparent',
          highlightColor:
            selectedSingleElement.tableData?.highlightColor || '#f0f0f0',
          highlightedRows:
            selectedSingleElement.tableData?.highlightedRows || [],
          highlightedColumns:
            selectedSingleElement.tableData?.highlightedColumns || [],
        });
      }

      // Update image properties
      if (isImageElement(selectedSingleElement)) {
        setImageProps({
          src: selectedSingleElement.src || '',
        });
      }

      // Update QR code properties
      if (isQRCodeElement(selectedSingleElement)) {
        setQrProps({
          qrValue: selectedSingleElement.qrValue || '',
        });
      }

      // Update barcode properties
      if (isBarcodeElement(selectedSingleElement)) {
        setBarcodeProps({
          barcodeValue: selectedSingleElement.barcodeValue || '',
        });
      }
    }
  }, [selectedSingleElement]);

  const updateElementProperty = useCallback(
    (key: string, value: any, updateType?: string) => {
      if (selectedSingleElement) {
        if (key === 'advancedTableCompound') {
          // For compound updates, the 'value' is the object with all changes
          onUpdateElement(
            selectedSingleElement.id,
            value as Partial<LabelElement>,
            'advancedTableCompound'
          );
        } else if (key === 'advancedTableData') {
          onUpdateElement(
            selectedSingleElement.id,
            {
              advancedTableData: value as AdvancedTableData,
            } as Partial<LabelElement>,
            updateType || key
          );
        } else if (key === 'barcodeData') {
          onUpdateElement(
            selectedSingleElement.id,
            { barcodeData: value } as Partial<LabelElement>,
            updateType || key
          );
        } else if (
          key === 'tableData' &&
          isTableElement(selectedSingleElement)
        ) {
          const currentTableData = selectedSingleElement.tableData || {
            rows: tableProps.rows,
            columns: tableProps.columns,
            cellWidth: selectedSingleElement.width / tableProps.columns,
            cellHeight: selectedSingleElement.height / tableProps.rows,
            borderColor: selectedSingleElement.stroke || '#000000',
            borderWidth: selectedSingleElement.lineWidth || 1,
            showGrid: tableProps.showGrid,
            headerTexts: tableProps.headerTexts,
            headerRow: tableProps.headerRow,
            headerRowHeight: tableProps.headerRowHeight,
            headerFontSize: tableProps.headerFontSize,
            headerTextColor: tableProps.headerTextColor,
            headerBackgroundColor: tableProps.headerBackgroundColor,
            backgroundColor: tableProps.backgroundColor,
            highlightColor: tableProps.highlightColor,
            highlightedRows: tableProps.highlightedRows,
            highlightedColumns: tableProps.highlightedColumns,
          };

          const updatedTableData = {
            ...currentTableData,
            ...value,
          };

          if (value.rows !== undefined && value.rows > 0) {
            updatedTableData.cellHeight =
              selectedSingleElement.height / value.rows;
          }
          if (value.columns !== undefined && value.columns > 0) {
            updatedTableData.cellWidth =
              selectedSingleElement.width / value.columns;
            // Update header texts array length if columns change
            if (updatedTableData.headerTexts) {
              if (value.columns > updatedTableData.headerTexts.length) {
                updatedTableData.headerTexts = [
                  ...updatedTableData.headerTexts,
                  ...Array(
                    value.columns - updatedTableData.headerTexts.length
                  ).fill('Header'),
                ];
              } else {
                updatedTableData.headerTexts =
                  updatedTableData.headerTexts.slice(0, value.columns);
              }
            }
          }

          onUpdateElement(
            selectedSingleElement.id,
            { tableData: updatedTableData },
            'tableData'
          );
        } else if (key === 'width' || key === 'height') {
          // For simple, common properties like width/height, direct assignment is fine.
          onUpdateElement(
            selectedSingleElement.id,
            { [key]: value as number },
            updateType || key
          );
        } else {
          // Default handling for other properties if necessary
          onUpdateElement(
            selectedSingleElement.id,
            { [key]: value },
            updateType || key
          );
        }
      }
    },
    [selectedSingleElement, onUpdateElement, tableProps]
  );

  const handleRotate = (direction: 'left' | 'right') => {
    const currentRotation = commonProps.rotation;
    const rotationAmount = direction === 'left' ? -90 : 90;
    const newRotation = currentRotation + rotationAmount;

    // Update local state for immediate visual feedback
    setCommonProps((prev) => ({
      ...prev,
      rotation: newRotation,
    }));

    // Update the element
    updateElementProperty('rotation', newRotation);
  };

  // Prevent backspace from triggering element deletion
  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Backspace' || e.key === 'Delete') {
      e.stopPropagation();
    }
  };

  return (
    <Paper
      sx={{
        width: 280,
        p: 2,
        display: 'flex',
        flexDirection: 'column',
        borderLeft: '1px solid',
        borderColor: 'divider',
        overflow: 'auto',
      }}
      onKeyDown={handleKeyDown}
    >
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          mb: 2,
        }}
      >
        <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
          Properties
        </Typography>
      </Box>

      {!hasMultipleSelection && !selectedSingleElement && (
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            height: '100%',
          }}
        >
          <Typography variant="body2" color="text.secondary">
            Select an element to edit its properties
          </Typography>
        </Box>
      )}

      {selectedSingleElement && (
        <>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            {selectedSingleElement.type.charAt(0).toUpperCase() +
              selectedSingleElement.type.slice(1)}{' '}
            Element
          </Typography>

          {(isTextElement(selectedSingleElement) ||
            isBarcodeElement(selectedSingleElement) ||
            isQRCodeElement(selectedSingleElement)) && (
            <DynamicAutocomplete
              selectedElement={selectedSingleElement}
              updateElementProperty={updateElementProperty}
              availableFields={availableFields}
            />
          )}

          <Typography variant="subtitle2" sx={{ mb: 1 }}>
            Element Actions
          </Typography>
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            <Tooltip title="Bring to Front">
              <div>
                <FBOButton
                  variant="tertiary"
                  color="neutral"
                  onClick={onBringToFront}
                  data-qa="bring-to-front-button"
                  icon={IconNames.FBOArrowUp}
                />
              </div>
            </Tooltip>
            <Tooltip title="Send to Back">
              <div>
                <FBOButton
                  variant="tertiary"
                  color="neutral"
                  onClick={onSendToBack}
                  data-qa="send-to-back-button"
                  icon={IconNames.FBOArrowDown}
                />
              </div>
            </Tooltip>
            <Tooltip title="Duplicate">
              <div>
                <FBOButton
                  variant="tertiary"
                  color="neutral"
                  onClick={onDuplicateElement}
                  data-qa="duplicate-element-button"
                  icon={IconNames.FBOCopy}
                />
              </div>
            </Tooltip>
            <Tooltip title="Rotate Left">
              <div>
                <FBOButton
                  variant="tertiary"
                  color="neutral"
                  onClick={() => handleRotate('left')}
                  data-qa="rotate-left-button"
                  icon={IconNames.FBORotateLeft}
                />
              </div>
            </Tooltip>
            <Tooltip title="Rotate Right">
              <div>
                <FBOButton
                  variant="tertiary"
                  color="neutral"
                  onClick={() => handleRotate('right')}
                  data-qa="rotate-right-button"
                  icon={IconNames.FBORotateRight}
                />
              </div>
            </Tooltip>
            <Tooltip title="Delete">
              <div>
                <FBOButton
                  variant="tertiary"
                  color="negative"
                  onClick={onDeleteElement}
                  data-qa="delete-element-button"
                  icon={IconNames.FBODeleteSmall}
                />
              </div>
            </Tooltip>
          </Box>

          <Divider sx={{ my: 2 }} />

          {isQRCodeElement(selectedSingleElement) && (
            <QRCodeProperties
              selectedElement={selectedSingleElement}
              updateElementProperty={updateElementProperty}
              handleKeyDown={handleKeyDown}
              qrProps={qrProps}
              setQrProps={setQrProps}
            />
          )}

          {isBarcodeElement(selectedSingleElement) && (
            <BarcodeProperties
              selectedElement={selectedSingleElement}
              barcodeProps={barcodeProps}
              setBarcodeProps={setBarcodeProps}
              updateElementProperty={updateElementProperty}
              handleKeyDown={handleKeyDown}
            />
          )}

          {isTableElement(selectedSingleElement) && (
            <TableProperties
              tableProps={tableProps}
              updateElementProperty={updateElementProperty}
              handleKeyDown={handleKeyDown}
            />
          )}

          {isTextElement(selectedSingleElement) && (
            <TextProperties
              selectedElement={selectedSingleElement}
              updateElementProperty={updateElementProperty}
              handleKeyDown={handleKeyDown}
              textProps={textProps}
              setTextProps={setTextProps}
            />
          )}

          {isShapeElement(selectedSingleElement) && (
            <ShapeProperties
              selectedElement={selectedSingleElement}
              updateElementProperty={updateElementProperty}
              lineProps={lineProps}
              setLineProps={setLineProps}
            />
          )}

          {isImageElement(selectedSingleElement) && (
            <ImageProperties
              updateElementProperty={updateElementProperty}
              handleKeyDown={handleKeyDown}
              imageProps={imageProps}
              setImageProps={setImageProps}
            />
          )}
          {isGroupElement(selectedSingleElement) && (
            <GroupProperties unGroup={onUngroup} />
          )}
          {isAdvancedTableElement(selectedSingleElement) && (
            <AdvancedTableProperties
              advancedTableProps={
                selectedSingleElement.advancedTableData ||
                JSON.parse(JSON.stringify(DEFAULT_ADVANCED_TABLE_DATA))
              }
              updateElementProperty={updateElementProperty}
              handleKeyDown={handleKeyDown}
              canvasWidth={canvasWidth}
              currentElementWidth={selectedSingleElement?.width ?? 0}
            />
          )}
        </>
      )}
      {hasMultipleSelection && (
        <MultipleSelectedElementsProperties
          selectedElements={selectedElements}
          onUpdateMultipleElements={onUpdateMultipleElements}
        />
      )}
    </Paper>
  );
};

export default PropertiesPanel;
